import { ApiFetchError, apiFetch, type ApiFetchOptions } from "./api";
import type { OnboardingStatus } from "@workspace/api-client-react";

type GetToken = (options?: { skipCache?: boolean }) => Promise<string | null>;
type Request = <T = unknown>(path: string, options: ApiFetchOptions) => Promise<T>;

export async function getOnboardingStatus(getToken: GetToken, request: Request = apiFetch) {
  const token = await getFreshToken(getToken);
  try {
    return await request<OnboardingStatus>("/auth/onboarding-status", { authToken: token });
  } catch (error) {
    if (error instanceof ApiFetchError && error.status === 401) throw new OnboardingAuthenticationError();
    throw error;
  }
}

export class OnboardingAuthenticationError extends Error {
  constructor() {
    super("Your session has expired. Please sign in again to finish setting up your account.");
    this.name = "OnboardingAuthenticationError";
  }
}

async function getFreshToken(getToken: GetToken): Promise<string> {
  try {
    const token = await getToken({ skipCache: true });
    if (!token) throw new OnboardingAuthenticationError();
    return token;
  } catch (error) {
    if (error instanceof OnboardingAuthenticationError) throw error;
    throw new OnboardingAuthenticationError();
  }
}

async function authenticatedRequest(
  getToken: GetToken,
  request: Request,
  path: string,
  options: ApiFetchOptions,
): Promise<void> {
  const token = await getFreshToken(getToken);
  try {
    await request(path, { ...options, authToken: token });
  } catch (error) {
    if (error instanceof ApiFetchError && error.status === 401) {
      throw new OnboardingAuthenticationError();
    }
    throw error;
  }
}

export async function submitOnboarding({
  registrationBody,
  legalBody,
  getToken,
  request = apiFetch,
}: {
  registrationBody: object;
  legalBody: object;
  getToken: GetToken;
  request?: Request;
}): Promise<void> {
  // Re-read on every attempt, including when a registration response was lost.
  const status = await getOnboardingStatus(getToken, request);
  if (!status.provisioned) {
    await authenticatedRequest(getToken, request, "/auth/register", {
      method: "POST",
      body: JSON.stringify(registrationBody),
    });
  }
  if (status.provisioned && status.missingDocTypes.length === 0) return;

  // Fetch a token again because registration can take long enough for a
  // just-created Clerk session token to rotate before legal acceptance.
  await authenticatedRequest(getToken, request, "/legal/accept", {
    method: "POST",
    body: JSON.stringify({ ...legalBody, signupRecovery: true }),
  });
}